import { useState, useEffect, useCallback } from "react";
import { QRCodeSVG } from "qrcode.react";
import { api, errorText, tokenStore } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Label, Field, Btn, Pill, Notice, Table, when } from "@/webapp/shared/ui";

function RecoveryCodes({ codes, onDone }) {
  const download = () => {
    const blob = new Blob([`FleetPods recovery codes\n\n${codes.join("\n")}\n\nEach code works once.`], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "fleetpods-recovery-codes.txt";
    a.click();
  };
  return (
    <div className="space-y-4" data-testid="sec-recovery-codes">
      <Notice tone="ok">Save these recovery codes somewhere safe. They are shown once and each works a single time.</Notice>
      <div className="grid grid-cols-2 gap-2 rounded-xl bg-[#0A234A] p-4 font-mono text-sm tracking-wider text-white sm:grid-cols-5">
        {codes.map((c) => <span key={c} data-testid="sec-recovery-code">{c}</span>)}
      </div>
      <div className="flex gap-2">
        <Btn testid="sec-recovery-download" onClick={download}>Download</Btn>
        <Btn testid="sec-recovery-copy" onClick={() => navigator.clipboard?.writeText(codes.join("\n"))}>Copy</Btn>
        <Btn primary testid="sec-recovery-done" onClick={onDone}>I've saved them</Btn>
      </div>
    </div>
  );
}

function MfaSetup({ onEnabled }) {
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const start = async () => { setErr(""); try { setSetup((await api.post("/auth/mfa/setup")).data); } catch (e) { setErr(errorText(e)); } };
  const enable = async () => {
    setBusy(true); setErr("");
    try { onEnabled((await api.post("/auth/mfa/enable", { code: code.trim() })).data.recovery_codes); } catch (e) { setErr(errorText(e)); }
    setBusy(false);
  };
  if (!setup) return (<div className="space-y-3">{err && <Notice>{err}</Notice>}<Btn primary testid="sec-mfa-start" onClick={start}>Set up authenticator</Btn></div>);
  return (
    <div className="grid gap-6 md:grid-cols-[auto,1fr]" data-testid="sec-mfa-setup">
      <div className="rounded-xl border border-slate-200 bg-white p-3"><QRCodeSVG value={setup.otpauth_uri} size={168} data-testid="sec-mfa-qr" /></div>
      <div className="space-y-3">
        <p className="text-sm text-slate-600">Scan with Google Authenticator, 1Password, Authy or Microsoft Authenticator. Can't scan? Enter this key manually:</p>
        <p className="break-all rounded-lg bg-[#F7F8FA] px-3 py-2 font-mono text-xs tracking-wider text-[#0A234A]" data-testid="sec-mfa-secret">{setup.secret}</p>
        <div><Label>6-digit code</Label><Field data-testid="sec-mfa-code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456" /></div>
        {err && <Notice testid="sec-mfa-error">{err}</Notice>}
        <Btn primary testid="sec-mfa-enable" onClick={enable} disabled={busy || code.length !== 6}>{busy ? "Verifying…" : "Verify and turn on"}</Btn>
      </div>
    </div>
  );
}

function MfaManage({ status, reload, onCodes }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const act = async (path) => {
    setErr("");
    try {
      const { data } = await api.post(path, { code: code.trim() });
      setCode("");
      if (data.recovery_codes) onCodes(data.recovery_codes);
      await reload();
    } catch (e) { setErr(errorText(e)); }
  };
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">Turned on {when(status.enabled_at)} · <span data-testid="sec-recovery-remaining">{status.recovery_remaining}</span> recovery codes left.</p>
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-40"><Label>Current code</Label><Field data-testid="sec-mfa-manage-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" /></div>
        <Btn testid="sec-mfa-regenerate" onClick={() => act("/auth/mfa/recovery-codes")} disabled={!code}>New recovery codes</Btn>
        {!status.mfa_required && <Btn danger testid="sec-mfa-disable" onClick={() => act("/auth/mfa/disable")} disabled={!code}>Turn off</Btn>}
      </div>
      {err && <Notice testid="sec-mfa-manage-error">{err}</Notice>}
    </div>
  );
}

function MfaCard() {
  const { checkAuth } = useAuth();
  const [status, setStatus] = useState(null);
  const [codes, setCodes] = useState(null);
  const load = useCallback(async () => { try { setStatus((await api.get("/auth/mfa/status")).data); } catch { setStatus(null); } }, []);
  useEffect(() => { load(); }, [load]);
  const enabled = async (c) => { setCodes(c); await load(); await checkAuth(); };
  return (
    <Card className="p-6" testid="sec-mfa-card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-display text-lg font-bold text-[#0A234A]">Authenticator app</p>
          <p className="text-sm text-slate-500">Time-based one-time codes (TOTP). Secrets are AES-256-GCM encrypted in the isolated vault.</p>
        </div>
        {status && <Pill testid="sec-mfa-status" tone={status.mfa_enabled ? "bg-emerald-50 text-emerald-700" : status.mfa_required ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-700"}>{status.mfa_enabled ? "On" : status.mfa_required ? "Required" : "Off"}</Pill>}
      </div>
      {codes ? <RecoveryCodes codes={codes} onDone={() => setCodes(null)} /> : status?.mfa_enabled ? <MfaManage status={status} reload={load} onCodes={setCodes} /> : status && <MfaSetup onEnabled={enabled} />}
    </Card>
  );
}

function SessionsCard() {
  const { logout } = useAuth();
  const [rows, setRows] = useState([]);
  const load = useCallback(async () => { try { setRows((await api.get("/auth/sessions")).data.sessions || []); } catch { setRows([]); } }, []);
  useEffect(() => { load(); }, [load]);
  const revoke = async (s) => {
    if (s.current && !window.confirm("This is your current session. Revoking it signs you out now.")) return;
    await api.delete(`/auth/sessions/${s.id}`);
    if (s.current) { tokenStore.clear(); await logout(); } else load();
  };
  const everywhere = async () => { await api.post("/auth/logout-all"); tokenStore.clear(); await logout(); };
  const active = rows.filter((s) => s.active);
  return (
    <Card className="p-6" testid="sec-sessions-card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-display text-lg font-bold text-[#0A234A]">Active sessions</p>
          <p className="text-sm text-slate-500">Every sign-in is a revocable server-side session with idle and absolute timeouts.</p>
        </div>
        <Btn danger testid="sec-logout-all" onClick={everywhere}>Sign out everywhere</Btn>
      </div>
      <Table head={["Device", "IP", "Last active", "Expires", "MFA", ""]} testid="sec-sessions-table">
        {active.map((s) => (
          <tr key={s.id} data-testid="sec-session-row">
            <td className="px-4 py-3"><div className="flex max-w-[320px] items-center gap-2">{s.current && <Pill testid="sec-session-current" tone="flex-shrink-0 bg-[#FF5722]/10 text-[#FF5722]">This device</Pill>}<span className="truncate text-slate-600" title={s.user_agent}>{s.user_agent || "Unknown"}</span></div></td>
            <td className="px-4 py-3 font-mono text-xs text-slate-500">{s.ip}</td>
            <td className="px-4 py-3 text-slate-600">{when(s.last_seen)}</td>
            <td className="px-4 py-3 text-slate-600">{when(s.expires_at)}</td>
            <td className="px-4 py-3">{s.mfa ? <Pill tone="bg-emerald-50 text-emerald-700">Verified</Pill> : <Pill>Password</Pill>}</td>
            <td className="px-4 py-3 text-right"><Btn ghost testid={`sec-session-revoke-${s.id}`} onClick={() => revoke(s)}>Revoke</Btn></td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}

function EventsCard({ isAdmin }) {
  const [scope, setScope] = useState("mine");
  const [events, setEvents] = useState([]);
  useEffect(() => { api.get(`/security/events?scope=${scope}&limit=60`).then((r) => setEvents(r.data.events || [])).catch(() => setEvents([])); }, [scope]);
  return (
    <Card className="p-6" testid="sec-events-card">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="font-display text-lg font-bold text-[#0A234A]">Security log</p>
          <p className="text-sm text-slate-500">Hash-chained and tamper-evident. Each entry seals the one before it.</p>
        </div>
        {isAdmin && <div className="inline-flex rounded-lg border border-slate-200 p-0.5">{["mine", "all"].map((s) => <button key={s} data-testid={`sec-events-scope-${s}`} onClick={() => setScope(s)} className={`rounded-md px-3 py-1 text-xs font-bold ${scope === s ? "bg-[#0A234A] text-white" : "text-slate-500"}`}>{s === "mine" ? "My account" : "All accounts"}</button>)}</div>}
      </div>
      <Table head={["Time", "Event", scope === "all" ? "Account" : "IP", "Outcome", "Seal"]} testid="sec-events-table">
        {events.map((e) => (
          <tr key={e.id} data-testid="sec-event-row">
            <td className="whitespace-nowrap px-4 py-2.5 text-slate-600">{when(e.ts)}</td>
            <td className="px-4 py-2.5 font-mono text-xs font-semibold text-[#0A234A]">{e.event}</td>
            <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{scope === "all" ? e.email || "—" : e.ip}</td>
            <td className="px-4 py-2.5"><Pill tone={e.outcome === "ok" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}>{e.outcome}</Pill></td>
            <td className="px-4 py-2.5 font-mono text-[11px] text-slate-400">{String(e.hash).slice(0, 12)}</td>
          </tr>
        ))}
      </Table>
      {!events.length && <p className="px-4 py-6 text-sm text-slate-400">No security events yet.</p>}
    </Card>
  );
}

function PolicyCard({ sessionMfa }) {
  const [policy, setPolicy] = useState(null);
  const [chain, setChain] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/security/policy").then((r) => setPolicy(r.data)).catch(() => {}); }, []);
  const toggle = async () => { setErr(""); try { setPolicy({ ...policy, ...(await api.put("/security/policy", { mfa_required_privileged: !policy.mfa_required_privileged })).data }); } catch (e) { setErr(errorText(e)); } };
  const verify = async () => { setErr(""); try { setChain((await api.get("/security/chain")).data); } catch (e) { setErr(errorText(e)); } };
  return (
    <Card className="p-6" testid="sec-policy-card">
      <p className="font-display text-lg font-bold text-[#0A234A]">Platform policy</p>
      <p className="mb-4 text-sm text-slate-500">Government and administrator sessions: 15-minute idle timeout, 8-hour hard limit.</p>
      <div className="flex flex-wrap items-center gap-3">
        <Btn primary={!policy?.mfa_required_privileged} testid="sec-policy-mfa-toggle" onClick={toggle} disabled={!policy || !sessionMfa}>{policy?.mfa_required_privileged ? "Stop requiring MFA" : "Require MFA for gov & admin"}</Btn>
        <Pill testid="sec-policy-mfa-state" tone={policy?.mfa_required_privileged ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}>{policy?.mfa_required_privileged ? "Enforced" : "Not enforced"}</Pill>
        <Btn testid="sec-chain-verify" onClick={verify}>Verify log integrity</Btn>
        {chain && <Pill testid="sec-chain-result" tone={chain.intact ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}>{chain.intact ? `Intact · ${chain.checked} entries` : `Broken at #${chain.broken_at_seq}`}</Pill>}
      </div>
      {!sessionMfa && <p className="mt-3 text-xs font-semibold text-slate-400">Turn on your own authenticator and sign in with it to change policy.</p>}
      {err && <div className="mt-3"><Notice>{err}</Notice></div>}
    </Card>
  );
}

export default function SecurityCenter() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  return (
    <div className="space-y-6" data-testid="security-center">
      <div>
        <span className="mb-2 block h-1 w-9 rounded-full bg-[#FF5722]" />
        <h1 className="font-display text-3xl font-bold tracking-tight text-[#0A234A]">Security</h1>
        <p className="mt-2 max-w-2xl text-[15px] text-slate-500">Two-step verification, live sessions and a tamper-evident record of every sign-in and sensitive action on your account.</p>
      </div>
      {user?.mfa_required && !user?.session_mfa && <Notice testid="sec-mfa-required-banner">Your organisation requires an authenticator for this account. Set it up below to unlock the console.</Notice>}
      <MfaCard />
      {isAdmin && <PolicyCard sessionMfa={!!user?.session_mfa} />}
      <SessionsCard />
      <EventsCard isAdmin={isAdmin} />
    </div>
  );
}
